import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'

import axios from 'axios'

import { COUNTRY_HEADER, countryPath, getCountryCode, migrateCountryScopedStorage } from '@/utils/country'
import apiClient from '@/libs/apiClient'

// Types
export interface User {
  id: string
  name: string
  email?: string | null
  mobile: string
  role: string
  avatar: string
  mobile_verified: boolean
  kyc_verified: boolean
  expires_at: string
  email_verified_at?: string | null
  mobile_verified_at?: string | null
}

interface AuthState {
  user: User | null
  accessToken: string | null
  permissions: string[]
  isAuthenticated: boolean
  loading: boolean
}

interface AuthContextValue extends AuthState {
  login: (mobile: string, password: string) => Promise<void>
  logout: () => void
  updateUser: (data: Partial<User>) => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// localStorage keys — shared by every country: the backend keeps one database
// with shared users, so one session works everywhere
const BASE_STORAGE_KEYS = ['accessToken', 'user', 'permissions'] as const

// Recover sessions stored by the earlier per-country build
migrateCountryScopedStorage(BASE_STORAGE_KEYS)

const STORAGE_KEYS = {
  token: 'accessToken',
  user: 'user',
  permissions: 'permissions'
} as const

function loadStoredAuth(): { user: User | null; accessToken: string | null; permissions: string[] } {
  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.token)
    const userJson = localStorage.getItem(STORAGE_KEYS.user)
    const permissionsJson = localStorage.getItem(STORAGE_KEYS.permissions)

    return {
      accessToken,
      user: userJson ? JSON.parse(userJson) : null,
      permissions: permissionsJson ? JSON.parse(permissionsJson) : []
    }
  } catch {
    return { user: null, accessToken: null, permissions: [] }
  }
}

function clearStoredAuth(): void {
  localStorage.removeItem(STORAGE_KEYS.token)
  localStorage.removeItem(STORAGE_KEYS.user)
  localStorage.removeItem(STORAGE_KEYS.permissions)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  // Hydrate from localStorage on mount
  useEffect(() => {
    const stored = loadStoredAuth()

    if (stored.accessToken && stored.user) {
      setAccessToken(stored.accessToken)
      setUser(stored.user)
      setPermissions(stored.permissions)
    }

    setLoading(false)
  }, [])

  const logout = useCallback(() => {
    clearStoredAuth()
    setUser(null)
    setAccessToken(null)
    setPermissions([])
    window.location.href = countryPath('/en/login')
  }, [])

  const login = useCallback(async (mobile: string, password: string) => {
    const baseURL = import.meta.env.VITE_API_URL

    const response = await axios.post(
      `${baseURL}/api/auth/login`,
      { mobile, password },
      { headers: { [COUNTRY_HEADER]: getCountryCode() } }
    )

    const { data } = response.data

    const userData: User = {
      id: data.user_data.id,
      name: data.user_data.name,
      mobile: data.user_data.mobile,
      role: data.user_data.role,
      avatar: typeof data.user_data.avatar === 'object' && data.user_data.avatar !== null 
        ? data.user_data.avatar.url 
        : data.user_data.avatar,
      mobile_verified: data.mobile_verified,
      kyc_verified: data.kyc_verified,
      expires_at: data.expires_at
    }

    const token = data.access_token as string
    const perms = (data.permissions ?? []) as string[]

    // Persist to localStorage
    localStorage.setItem(STORAGE_KEYS.token, token)
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(userData))
    localStorage.setItem(STORAGE_KEYS.permissions, JSON.stringify(perms))

    // Update state
    setAccessToken(token)
    setUser(userData)
    setPermissions(perms)
  }, [])

  const updateUser = useCallback((data: Partial<User>) => {
    setUser(prev => {
      if (!prev) return null
      const newUser = { ...prev, ...data }
      localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(newUser))
      return newUser
    })
  }, [])

  // The avatar is a signed S3 link that expires, so reload the profile from the
  // server (on start, and when an avatar fails to load — at most once a minute)
  const lastRefreshRef = useRef(0)

  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem(STORAGE_KEYS.token) || Date.now() - lastRefreshRef.current < 60_000) return

    lastRefreshRef.current = Date.now()

    try {
      const res = await apiClient.get('/admin/v1/profile')
      const fresh = res.data?.data

      if (fresh) {
        updateUser({
          name: fresh.name,
          email: fresh.email,
          mobile: fresh.mobile,
          avatar: fresh.avatar || '',

          // The profile page's Verified/Unverified badges read these
          email_verified_at: fresh.email_verified_at ?? null,
          mobile_verified_at: fresh.mobile_verified_at ?? null
        })
      }
    } catch {
      // Keep the stored user; a failed refresh must not log anyone out
    }
  }, [updateUser])

  useEffect(() => {
    if (accessToken) refreshUser()
  }, [accessToken, refreshUser])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      permissions,
      isAuthenticated: !!accessToken && !!user,
      loading,
      login,
      logout,
      updateUser,
      refreshUser
    }),
    [user, accessToken, permissions, loading, login, logout, updateUser, refreshUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)

  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return ctx
}
